import { CardsContainer } from "shared/components/CardsContainer";
import { Content } from "shared/components/Content";
import { DISCIPLINE_TYPE, LARGE } from "shared/utils/constants";
import { PROJECT_OVERVIEW_TYPE } from "clariah/utils/constants";
import { FlexContainerWithTitle } from "shared/components/FlexContainerWithTitle";
import { MaxWidth } from "shared/components/MaxWidth";
import { MetaGrid } from "shared/components/MetaGrid";
import { Page } from "clariah/components/Page";
import { ProjectHero } from "shared/components/ProjectHero";
import { Publications } from "shared/components/Publications";
import { ResearcherGrid } from "shared/components/ResearcherGrid";
import { Spacing } from "shared/components/Spacing";
import { types } from "clariah/utils/types";
import {
  RESEARCH_DOMAINS,
  DURATION,
  PARTNERS,
  PROJECT_INFO,
  PROJECT_PUBLICATIONS,
  PROJECT_RESEARCHERS,
  PRICIPAL_INVESTIGATOR,
  RESOURCE_TYPES,
  RESEARCH_ACTIVITIES,
  USED_RESOURCES,
  RELATED_PROJECTS,
  translate,
} from "shared/utils/translate";

export const ProjectDetailPage = ({
  _type,
  body,
  researchDomains,
  duration,
  footer,
  heroImage,
  intro,
  locale,
  mirrorPath,
  navigation,
  partners,
  publications,
  related,
  researchers,
  researchActivities,
  resources,
  resourceTypes,
  seo,
  title,
}) => {
  researchers = researchers ? researchers.filter((x) => x) : [];
  researchDomains = researchDomains ? researchDomains.filter((x) => x) : [];
  resourceTypes = resourceTypes ? resourceTypes.filter((x) => x) : [];
  researchActivities = researchActivities
    ? researchActivities.filter((x) => x)
    : [];

  const projectInfoMeta = getMeta({
    duration,
    researchDomains,
    partners,
    resources,
    locale,
  });

  return (
    <Page
      darkHeader
      description={seo?.description || intro}
      footer={footer}
      image={seo?.image}
      mirrorPath={mirrorPath}
      navigation={navigation}
      title={seo?.title || title}
    >
      <ProjectHero
        type={_type}
        title={title}
        lead={intro}
        image={heroImage}
        meta={[
          {
            title: translate(PRICIPAL_INVESTIGATOR, locale),
            items: (researchers || []).slice(0, 1).map(({ name }) => ({
              label: name,
            })),
          },
          {
            title: translate(RESEARCH_DOMAINS, locale),
            items: (researchDomains || []).slice(0, 2).map(({ title }) => ({
              label: title,
            })),
          },
          {
            title: translate(RESOURCE_TYPES, locale),
            items: (resourceTypes || []).slice(0, 3).map(({ title }) => ({
              label: title,
            })),
          },
          {
            title: translate(RESEARCH_ACTIVITIES, locale),
            items: (researchActivities || []).slice(0, 3).map(({ title }) => ({
              label: title,
            })),
          },
        ].filter(({ items }) => items.length)}
      />

      <Spacing>
        <Content data={body} />
      </Spacing>

      <Spacing>
        {projectInfoMeta?.length > 0 && (
          <FlexContainerWithTitle title={translate(PROJECT_INFO, locale)}>
            <MetaGrid meta={projectInfoMeta} />
          </FlexContainerWithTitle>
        )}

        {researchers?.length > 0 && (
          <FlexContainerWithTitle
            title={translate(PROJECT_RESEARCHERS, locale)}
          >
            <ResearcherGrid researchers={researchers.filter((x) => x)} />
          </FlexContainerWithTitle>
        )}
        {publications?.length > 0 && (
          <FlexContainerWithTitle
            title={translate(PROJECT_PUBLICATIONS, locale)}
          >
            <Publications publications={publications.filter((x) => x)} />
          </FlexContainerWithTitle>
        )}
        {related?.length > 0 && (
          <MaxWidth size={LARGE}>
            <CardsContainer
              allLinkType={_type}
              items={related.filter((x) => x)}
              title={translate(RELATED_PROJECTS, locale)}
            />
          </MaxWidth>
        )}
      </Spacing>
    </Page>
  );
};

function getMeta({ duration, disciplines, partners, resources, locale }) {
  const meta = [];

  if (duration) {
    meta.push({
      title: translate(DURATION, locale),
      items: [{ label: duration }],
    });
  }

  if (disciplines) {
    meta.push({
      title: translate(RESEARCH_DOMAINS, locale),
      items: disciplines.filter(Boolean).map(({ name, slug }) => ({
        href: `${types[PROJECT_OVERVIEW_TYPE][locale]}?disciplines=${slug.current}`,
        label: name,
        type: DISCIPLINE_TYPE,
      })),
    });
  }

  if (partners) {
    meta.push({
      title: translate(PARTNERS, locale),
      items: partners.filter(Boolean).map(({ name, website }) => ({
        href: website,
        label: name,
      })),
    });
  }

  if (resources) {
    meta.push({
      title: translate(USED_RESOURCES, locale),
      items: resources,
    });
  }

  return meta;
}
